import { Fragment } from "react";
import styled from "styled-components";

import ExecutionResult from "app/components/analytics/shared/ResultIcons/ExecutionResult";
import FailureReason from "app/components/analytics/shared/FailureReason";
import useLoadMore, { LoadMoreProps } from "../../shared/hooks/useLoadMore";
import LoadMoreButton from "../../shared/LoadMoreButton";
import BuildNumber from "../../shared/Metadata/BuildNumber";
import { getDateString } from "app/lib/date";
import { Execution } from "app/components/analytics/shared/type";

export type FlakyExecution = Omit<
  Execution,
  "duration" | "humanDuration" | "uuid"
>;

type ExecutionsProps = {
  executions: Array<FlakyExecution>;
  nextPage?: string;
};

type ExecutionsLoadMoreProps = Omit<LoadMoreProps, "collection"> & {
  collection: Array<FlakyExecution>;
};

const ExecutionContainer = styled.div`
  padding: 20px 30px;
`;

const ExecutionTable = styled.table`
  margin: 0;
  max-width: 100%;
  border: none;
  display: block;
  border-bottom: 0px !important;

  tbody {
    display: block;
  }

  tr {
    display: flex;
  }
`;

const MetadataRow = styled.tr`
  td {
    padding: 10px 4px 0px 4px;
    z-index: 1;
    font-size: 13px;
    color: var(--charcoal-700);
  }

  td:first-child {
    padding-left: 0px;
  }

  td:last-child:not(:empty):before {
    padding-right: 8px;
    content: "·";
    color: var(--gray-700);
  }
`;

const FailureReasonRow = styled.tr`
  td:first-child {
    width: 100%;
    padding-left: 30px;
  }
`;

const ExecutionsList = ({ executions, nextPage }: ExecutionsProps) => {
  const {
    collection,
    handleClickLoadMore,
    hasNextPage,
    loading,
  }: ExecutionsLoadMoreProps = useLoadMore({ data: executions, url: nextPage });

  return (
    <ExecutionContainer>
      <ExecutionTable>
        <tbody>
          {collection.map((execution) => (
            <Fragment key={execution.id}>
              <MetadataRow>
                <td>
                  <ExecutionResult.Small
                    result={execution.result.toUpperCase()}
                  />
                </td>
                {execution.createdAt && (
                  <td>
                    <a className="color-inherit" href={execution.url}>
                      {getDateString(execution.createdAt, {
                        utc: true,
                        withFractionalSeconds: false,
                      })}
                    </a>
                  </td>
                )}
                <td>
                  <BuildNumber
                    number={execution.buildNumber}
                    url={execution.buildUrl}
                  />
                </td>
              </MetadataRow>
              {execution.failureReason != null && (
                <FailureReasonRow>
                  <td colSpan={3} className="p0">
                    <FailureReason
                      failureReason={execution.failureReason}
                      endpoint={execution.url}
                      failureExpanded={execution.failureExpanded}
                    />
                  </td>
                </FailureReasonRow>
              )}
            </Fragment>
          ))}
        </tbody>
      </ExecutionTable>

      {hasNextPage && (
        <LoadMoreButton
          loading={loading}
          collectionName="executions"
          handleLoadMore={handleClickLoadMore}
        />
      )}
    </ExecutionContainer>
  );
};

export default ExecutionsList;
